import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { fetchJsonCached, getCachedJson } from '../utils/portalQueryCache';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import { RoundedSelect } from '../components/RoundedSelect';

function getPlatformUserDisplayName(entry) {
  return [entry?.firstName, entry?.lastName].filter(Boolean).join(' ') || 'No name';
}

function PlatformUserAvatar({ entry }) {
  const [error, setError] = useState(false);
  const thumbUrl = String(entry?.avatarThumbUrl || '').trim();
  const fullUrl = String(entry?.avatarUrl || '').trim();
  const url = String((error && fullUrl && thumbUrl && fullUrl !== thumbUrl ? fullUrl : thumbUrl || fullUrl) || '').trim();
  const label = getPlatformUserDisplayName(entry);
  const initial = (label || entry?.email || 'U').slice(0, 1).toUpperCase();

  if (!url || error) {
    return (
      <div className="user-list-avatar-fallback" aria-hidden="true">
        {initial}
      </div>
    );
  }

  return (
    <img
      className="user-list-avatar-image"
      src={url}
      alt=""
      loading="lazy"
      onError={() => setError(true)}
    />
  );
}

function normalizeGroupScopes(entry) {
  const scopes = Array.isArray(entry?.groupScopes) ? entry.groupScopes : [];
  return scopes
    .map((s) => ({
      groupId: s?.groupId != null ? Number(s.groupId) : null,
      groupName: String(s?.groupName || '').trim(),
      storeId: s?.storeId != null ? Number(s.storeId) : null,
      storeName: String(s?.storeName || '').trim(),
      customerAccountName: String(s?.customerAccountName || '').trim(),
      roleCode: String(s?.roleCode || '').trim().toUpperCase(),
      roleLabel: String(s?.roleLabel || '').trim(),
    }))
    .filter((s) => Number.isFinite(s.groupId) && s.groupId > 0);
}

function filterGroupScopesForActor(scopes, { isHkAdmin, groupAdminGroupIds, storeAdminStoreIds }) {
  if (!scopes?.length || isHkAdmin) return scopes;
  const groupIds = Array.isArray(groupAdminGroupIds) ? groupAdminGroupIds : [];
  const storeIds = Array.isArray(storeAdminStoreIds) ? storeAdminStoreIds : [];
  if (groupIds.length === 0 && storeIds.length === 0) return [];
  return scopes.filter(
    (s) => groupIds.includes(s.groupId) || (s.storeId != null && storeIds.includes(Number(s.storeId)))
  );
}

function getRoleRank(roleCode) {
  const code = String(roleCode || '').toUpperCase();
  if (code === 'HK_ADMIN') return 5;
  if (code === 'GROUP_ADMIN') return 4;
  if (code === 'GENERAL_USER') return 3;
  if (code === 'STORE_ADMIN') return 2;
  if (code === 'SALESPERSON') return 1;
  return 0;
}

function canManageUserPlatformEntry(entry, { isHkAdmin, groupAdminGroupIds, storeAdminStoreIds }) {
  if (isHkAdmin) return true;
  const targetRole = String(entry?.roleCode || '').toUpperCase();
  if (!targetRole || targetRole === 'HK_ADMIN' || targetRole === 'GROUP_ADMIN') return false;

  const groupIds = Array.isArray(groupAdminGroupIds) ? groupAdminGroupIds.map(Number) : [];
  const storeIds = Array.isArray(storeAdminStoreIds) ? storeAdminStoreIds.map(Number) : [];
  const entryGroupId = entry?.groupId != null ? Number(entry.groupId) : null;
  const entryStoreId = entry?.storeId != null ? Number(entry.storeId) : null;

  if (entryGroupId && groupIds.includes(entryGroupId)) {
    return getRoleRank(targetRole) < getRoleRank('GROUP_ADMIN');
  }
  if (entryStoreId && storeIds.includes(entryStoreId)) {
    return targetRole === 'SALESPERSON' || targetRole === 'GENERAL_USER';
  }
  return false;
}

function isGroupAdminEntry(entry) {
  return String(entry?.roleCode || '').toUpperCase() === 'GROUP_ADMIN';
}

function isGeneralUserRoleEntry(entry) {
  return String(entry?.roleCode || '').toUpperCase() === 'GENERAL_USER';
}

function formatUserGroupLabel(entry) {
  return entry?.groupName || '';
}

function formatUserStoreLabel(entry) {
  if (isGroupAdminEntry(entry) || isGeneralUserRoleEntry(entry)) return 'All';
  return entry?.storeName || '';
}

function formatUserCustomerLabel(entry) {
  if (isGroupAdminEntry(entry)) return 'All';
  return entry?.customerAccountName || '';
}

function formatUserStatusLabel(entry) {
  return entry?.statusLabel || (entry?.isActive ? 'Active' : 'Inactive');
}

const PLATFORM_USER_COLUMNS = [
  { key: 'user', label: 'User' },
  { key: 'email', label: 'Email' },
  { key: 'status', label: 'Status' },
  { key: 'role', label: 'Role' },
  { key: 'group', label: 'Group' },
  { key: 'customer', label: 'Customer' },
  { key: 'store', label: 'Store' },
];

export function PlatformAccessPage() {
  const { user } = useAuth();
  const [mode, setMode] = useState('store'); // store | group | user
  const [users, setUsers] = useState(() => {
    const cached = getCachedJson('/api/admin/roles/users');
    if (Array.isArray(cached)) return cached;
    if (cached && Array.isArray(cached.items)) return cached.items;
    return [];
  });
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(() => (getCachedJson('/api/admin/roles/users') ? false : true));
  const [error, setError] = useState('');

  const [groups, setGroups] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [groupsPickerOpen, setGroupsPickerOpen] = useState(false);
  const [groupsPickerQuery, setGroupsPickerQuery] = useState('');
  const groupsPickerRef = useRef(null);
  const [storesPickerOpen, setStoresPickerOpen] = useState(false);
  const [storesPickerQuery, setStoresPickerQuery] = useState('');
  const [storeOptions, setStoreOptions] = useState(null);
  const storesPickerRef = useRef(null);

  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [platformsFor, setPlatformsFor] = useState(null);
  const [platformsList, setPlatformsList] = useState([]);
  const [platformsError, setPlatformsError] = useState('');
  const [platformSearch, setPlatformSearch] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [pendingPlatformIds, setPendingPlatformIds] = useState([]);
  const [userSort, setUserSort] = useState({ key: '', dir: null });

  const isHkAdmin = user?.role === 'HK-Admin';
  const isGeneralUser = useMemo(() => user?.role === 'General user', [user?.role]);
  const isGroupAdmin = useMemo(
    () => user?.role === 'Group-Admin' || (Array.isArray(user?.groupAdminGroupIds) && user.groupAdminGroupIds.length > 0),
    [user?.role, user?.groupAdminGroupIds]
  );
  const isGroupScopeAdmin = isGroupAdmin || isGeneralUser;
  const isStoreAdmin = useMemo(
    () => user?.role === 'Store-Admin' || (Array.isArray(user?.storeAdminStoreIds) && user.storeAdminStoreIds.length > 0),
    [user?.role, user?.storeAdminStoreIds]
  );
  const isPureStoreAdmin = isStoreAdmin && !isGroupScopeAdmin && !isHkAdmin;
  const canSee = useMemo(
    () => isHkAdmin || isGroupScopeAdmin || isStoreAdmin,
    [isHkAdmin, isGroupScopeAdmin, isStoreAdmin]
  );
  const canEditStoreScope = isHkAdmin || isGroupScopeAdmin;
  const storesScopeQuery = isHkAdmin ? '' : 'groupAdminScopeOnly=true';
  const debouncedSearch = useDebouncedValue(search, 250);
  const debouncedPlatformSearch = useDebouncedValue(platformSearch, 150);

  const actorGroupAdminGroupIds = useMemo(() => {
    const fromApi = Array.isArray(user?.groupAdminGroupIds)
      ? user.groupAdminGroupIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (fromApi.length > 0) return fromApi;
    const fromGeneralUser = Array.isArray(user?.generalUserGroupIds)
      ? user.generalUserGroupIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (fromGeneralUser.length > 0) return fromGeneralUser;
    const groupIdNum = user?.groupId != null ? Number(user.groupId) : null;
    return Number.isFinite(groupIdNum) && groupIdNum > 0 ? [groupIdNum] : [];
  }, [user?.groupAdminGroupIds, user?.generalUserGroupIds, user?.groupId]);

  const actorStoreAdminStoreIds = useMemo(() => {
    const fromApi = Array.isArray(user?.storeAdminStoreIds)
      ? user.storeAdminStoreIds.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
      : [];
    if (fromApi.length > 0) return fromApi;
    const storeIdNum = user?.storeId != null ? Number(user.storeId) : null;
    return Number.isFinite(storeIdNum) && storeIdNum > 0 ? [storeIdNum] : [];
  }, [user?.storeAdminStoreIds, user?.storeId]);

  const scopeFilterContext = useMemo(
    () => ({
      isHkAdmin,
      groupAdminGroupIds: actorGroupAdminGroupIds,
      storeAdminStoreIds: actorStoreAdminStoreIds,
    }),
    [isHkAdmin, actorGroupAdminGroupIds, actorStoreAdminStoreIds]
  );

  useEffect(() => {
    if (isPureStoreAdmin && mode !== 'user') {
      setMode('user');
    }
  }, [isPureStoreAdmin, mode]);

  useEffect(() => {
    if (!canSee) {
      setIsLoading(false);
      return;
    }

    let isCancelled = false;
    const controller = new AbortController();
    (async () => {
      setError('');
      try {
        // Preload groups/stores for scope selectors.
        const groupsPromise = isHkAdmin
          ? fetchJsonCached('/api/admin/roles/groups', { ttlMs: 30_000, signal: controller.signal }).catch(() => [])
          : Promise.resolve([]);
        const storesUrl = storesScopeQuery
          ? `/api/admin/roles/stores?${storesScopeQuery}`
          : '/api/admin/roles/stores';
        const storesPromise = isPureStoreAdmin
          ? Promise.resolve([])
          : fetchJsonCached(storesUrl, { ttlMs: 30_000, signal: controller.signal }).catch(() => []);
        const [groupsData, storesData] = await Promise.all([groupsPromise, storesPromise]);
        if (!isCancelled) {
          setGroups(groupsData || []);
          setStores(storesData || []);
          setStoreOptions(storesData || []);
        }

        if (mode === 'user') {
          const usersUrl = `/api/admin/roles/users?search=${encodeURIComponent(debouncedSearch)}&page=1&pageSize=200`;
          const usersData = await fetchJsonCached(usersUrl, { ttlMs: 10_000, signal: controller.signal });
          if (!isCancelled) setUsers(Array.isArray(usersData?.items) ? usersData.items : (usersData || []));
        }
      } catch (e) {
        if (!isCancelled) setError(e.message || 'Failed to load users.');
      } finally {
        if (!isCancelled) setIsLoading(false);
      }
    })();

    return () => {
      isCancelled = true;
      controller.abort();
    };
  }, [canSee, debouncedSearch, mode, isHkAdmin, isPureStoreAdmin, storesScopeQuery]);

  useEffect(() => {
    if (mode !== 'store') return;
    const controller = new AbortController();
    const groupId = selectedGroupId ? Number(selectedGroupId) : null;
    const scopeSuffix = storesScopeQuery ? `&${storesScopeQuery}` : '';
    const url = groupId
      ? `/api/admin/roles/stores?groupId=${groupId}${scopeSuffix}`
      : storesScopeQuery
        ? `/api/admin/roles/stores?${storesScopeQuery}`
        : '/api/admin/roles/stores';
    fetch(url, { credentials: 'include', signal: controller.signal })
      .then((response) => response.json().catch(() => []))
      .then((data) => setStoreOptions(Array.isArray(data) ? data : []))
      .catch(() => setStoreOptions(groupId ? [] : stores));
    return () => controller.abort();
  }, [mode, selectedGroupId, stores, storesScopeQuery]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (groupsPickerRef.current && !groupsPickerRef.current.contains(event.target)) {
        setGroupsPickerOpen(false);
      }
      if (!storesPickerRef.current) return;
      if (!storesPickerRef.current.contains(event.target)) {
        setStoresPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  useEffect(() => {
    if (!groupsPickerOpen) {
      setGroupsPickerQuery('');
    }
  }, [groupsPickerOpen]);

  useEffect(() => {
    if (!storesPickerOpen) {
      setStoresPickerQuery('');
    }
  }, [storesPickerOpen]);

  useEffect(() => {
    if (!groupsPickerOpen) return;
    setStoresPickerOpen(false);
    if (platformsOpen) setPlatformsOpen(false);
  }, [groupsPickerOpen, platformsOpen]);

  useEffect(() => {
    if (!storesPickerOpen) return;
    setGroupsPickerOpen(false);
    if (platformsOpen) setPlatformsOpen(false);
  }, [storesPickerOpen, platformsOpen]);

  useEffect(() => {
    if (!platformsOpen) return;
    setGroupsPickerOpen(false);
    setStoresPickerOpen(false);
  }, [platformsOpen]);

  const filteredGroupsForPicker = useMemo(() => {
    const normalized = groupsPickerQuery.trim().toLowerCase();
    if (!normalized) return groups;
    return groups.filter((group) => String(group?.name || '').toLowerCase().includes(normalized));
  }, [groups, groupsPickerQuery]);

  const storesForSelectedGroup = useMemo(() => (storeOptions ?? stores), [storeOptions, stores]);

  const filteredStoresForPicker = useMemo(() => {
    const normalized = storesPickerQuery.trim().toLowerCase();
    if (!normalized) return storesForSelectedGroup;
    return storesForSelectedGroup.filter((store) => String(store?.name || '').toLowerCase().includes(normalized));
  }, [storesForSelectedGroup, storesPickerQuery]);

  useEffect(() => {
    const allowed = new Set(storesForSelectedGroup.map((s) => s.id));
    setSelectedStoreIds((current) => current.filter((id) => allowed.has(id)));
  }, [storesForSelectedGroup]);

  const platformUserRows = useMemo(() => {
    const rows = [];
    for (const entry of users) {
      const scopes = filterGroupScopesForActor(normalizeGroupScopes(entry), scopeFilterContext);
      if (!isHkAdmin && (isGroupScopeAdmin || isStoreAdmin) && scopes.length === 0) continue;
      if (scopes.length === 0) {
        const userRoleCode = String(entry.roleCode || '').toUpperCase();
        rows.push({
          ...entry,
          __rowKey: `${entry.userId}:default`,
          roleCode: userRoleCode === 'GENERAL_USER' ? 'GENERAL_USER' : (entry.roleCode || 'SALESPERSON'),
          roleLabel: userRoleCode === 'GENERAL_USER' ? 'General user' : (entry.roleLabel || entry.roleCode),
        });
        continue;
      }
      for (const s of scopes) {
        const scopeRoleCode = s.roleCode || (String(entry.roleCode || '').toUpperCase() === 'GENERAL_USER' ? 'GENERAL_USER' : 'SALESPERSON');
        const scopeRoleLabel = s.roleLabel || scopeRoleCode;
        const isScopeGroupAdmin = scopeRoleCode === 'GROUP_ADMIN';
        rows.push({
          ...entry,
          __rowKey: `${entry.userId}:g:${s.groupId}:s:${s.storeId || 0}`,
          groupId: s.groupId,
          groupName: s.groupName || entry.groupName,
          storeId: isScopeGroupAdmin ? null : s.storeId,
          storeName: isScopeGroupAdmin ? 'All' : (s.storeName || entry.storeName),
          customerAccountName: isScopeGroupAdmin ? 'All' : (s.customerAccountName || entry.customerAccountName),
          roleCode: scopeRoleCode,
          roleLabel: scopeRoleLabel,
        });
      }
    }
    return rows;
  }, [users, scopeFilterContext, isHkAdmin, isGroupScopeAdmin, isStoreAdmin]);

  const openPlatforms = async (entry) => {
    setPlatformsError('');
    setPlatformsList([]);
    setPlatformsFor({ ...entry, mode: 'user' });
    setPlatformsOpen(true);
    setIsBusy(true);
    try {
      const params = new URLSearchParams();
      if (entry.groupId != null) params.set('groupId', String(entry.groupId));
      if (entry.storeId != null) params.set('storeId', String(entry.storeId));
      const query = params.toString();
      const response = await fetch(
        `/api/admin/platforms/users/${entry.userId}${query ? `?${query}` : ''}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to load platforms.');
      }
      const data = await response.json();
      setPlatformsList(Array.isArray(data) ? data : []);
    } catch (e) {
      setPlatformsError(e.message || 'Failed to load platforms.');
    } finally {
      setIsBusy(false);
    }
  };

  const togglePlatform = async (platformId, nextEnabled) => {
    if (!platformsFor || pendingPlatformIds.includes(platformId)) return;
    setPlatformsError('');
    const previousList = platformsList;
    setPendingPlatformIds((current) => [...current, platformId]);
    setPlatformsList((current) =>
      current.map((p) => (p.platformId === platformId ? { ...p, isEnabled: nextEnabled, source: 'User' } : p))
    );
    try {
      const response = await fetch(`/api/admin/platforms/users/${platformsFor.userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          platformId,
          isEnabled: nextEnabled,
          storeId: platformsFor.storeId ?? null,
          groupId: platformsFor.groupId ?? null,
        }),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.message || 'Failed to update platform access.');
      }
    } catch (e) {
      setPlatformsList(previousList);
      setPlatformsError(e.message || 'Failed to update platform access.');
    } finally {
      setPendingPlatformIds((current) => current.filter((id) => id !== platformId));
    }
  };

  const computeAggregatePlatforms = (responses, scopeIds) => {
    const byPlatform = new Map();
    responses.forEach((list) => {
      (list || []).forEach((p) => {
        const existing = byPlatform.get(p.platformId) || {
          platformId: p.platformId,
          platformCode: p.platformCode,
          platformName: p.platformName,
          enabledCount: 0,
          total: 0,
        };
        existing.total += 1;
        if (p.isEnabled) existing.enabledCount += 1;
        byPlatform.set(p.platformId, existing);
      });
    });

    const result = Array.from(byPlatform.values()).map((x) => {
      const state = x.enabledCount === 0 ? 'disabled' : x.enabledCount === x.total ? 'enabled' : 'mixed';
      return {
        platformId: x.platformId,
        platformCode: x.platformCode,
        platformName: x.platformName,
        isEnabled: state === 'enabled',
        accessState: state,
        scopeIds,
      };
    });

    return result.sort((a, b) => String(a.platformName).localeCompare(String(b.platformName)));
  };

  const readApiErrorMessage = async (response, fallback) => {
    const payload = await response.json().catch(() => null);
    if (payload?.message) return payload.message;
    if (response.status === 403) return 'Access denied for selected scope. Check your role/group permissions.';
    return fallback;
  };

  const loadScopePlatforms = async ({ nextMode, groupId, groupIds, storeId, storeIds }) => {
    setPlatformsError('');
    setPlatformsList([]);
    setPlatformsFor({ mode: nextMode, groupId, groupIds, storeId, storeIds });
    setPlatformsOpen(true);
    setIsBusy(true);
    try {
      if (nextMode === 'group' && Array.isArray(groupIds) && groupIds.length > 0) {
        const responses = await Promise.all(
          groupIds.map(async (gid) => {
            const response = await fetch(`/api/admin/platforms/groups/${gid}`, { credentials: 'include' });
            if (!response.ok) return [];
            const data = await response.json().catch(() => []);
            return Array.isArray(data) ? data : [];
          })
        );
        setPlatformsList(computeAggregatePlatforms(responses, groupIds));
      } else if (nextMode === 'store' && Array.isArray(storeIds) && storeIds.length > 0) {
        const responses = await Promise.all(
          storeIds.map(async (sid) => {
            const response = await fetch(`/api/admin/platforms/stores/${sid}`, { credentials: 'include' });
            if (!response.ok) return [];
            const data = await response.json().catch(() => []);
            return Array.isArray(data) ? data : [];
          })
        );
        setPlatformsList(computeAggregatePlatforms(responses, storeIds));
      } else {
        const url = nextMode === 'group' ? `/api/admin/platforms/groups/${groupId}` : `/api/admin/platforms/stores/${storeId}`;
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) {
          const payload = await response.json().catch(() => null);
          throw new Error(payload?.message || 'Failed to load platforms.');
        }
        const data = await response.json();
        setPlatformsList(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setPlatformsError(e.message || 'Failed to load platforms.');
    } finally {
      setIsBusy(false);
    }
  };

  const saveScopePlatforms = async (updates) => {
    if (!platformsFor) return;
    setPlatformsError('');
    const previousList = platformsList;
    const updateIds = updates.map((u) => u.platformId);
    setPendingPlatformIds((current) => [...current, ...updateIds.filter((id) => !current.includes(id))]);
    setPlatformsList((current) =>
      current.map((p) => {
        const upd = updates.find((u) => u.platformId === p.platformId);
        if (!upd) return p;
        const state = upd.isEnabled ? 'enabled' : 'disabled';
        return { ...p, isEnabled: upd.isEnabled, accessState: state };
      })
    );
    try {
      if (platformsFor.mode === 'group' && Array.isArray(platformsFor.groupIds) && platformsFor.groupIds.length > 0) {
        await Promise.all(
          platformsFor.groupIds.map(async (gid) => {
            const response = await fetch(`/api/admin/platforms/groups/${gid}`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: updates }),
            });
            if (!response.ok) {
              const message = await readApiErrorMessage(response, 'Failed to update platform access.');
              throw new Error(`Group ${gid}: ${message}`);
            }
          })
        );
      } else if (platformsFor.mode === 'store' && Array.isArray(platformsFor.storeIds) && platformsFor.storeIds.length > 0) {
        await Promise.all(
          platformsFor.storeIds.map(async (sid) => {
            const response = await fetch(`/api/admin/platforms/stores/${sid}`, {
              method: 'PUT',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: updates }),
            });
            if (!response.ok) {
              const message = await readApiErrorMessage(response, 'Failed to update platform access.');
              throw new Error(`Store ${sid}: ${message}`);
            }
          })
        );
      } else {
        const url =
          platformsFor.mode === 'group'
            ? `/api/admin/platforms/groups/${platformsFor.groupId}`
            : `/api/admin/platforms/stores/${platformsFor.storeId}`;
        const response = await fetch(url, {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: updates }),
        });
        if (!response.ok) {
          const message = await readApiErrorMessage(response, 'Failed to update platform access.');
          throw new Error(message);
        }
      }
    } catch (e) {
      setPlatformsList(previousList);
      setPlatformsError(e.message || 'Failed to update platform access.');
    } finally {
      setPendingPlatformIds((current) => current.filter((id) => !updateIds.includes(id)));
    }
  };

  const filteredPlatformsList = useMemo(() => {
    const q = (debouncedPlatformSearch || '').trim().toLowerCase();
    if (!q) return platformsList;
    return platformsList.filter((p) => `${p.platformCode} ${p.platformName}`.toLowerCase().includes(q));
  }, [platformsList, debouncedPlatformSearch]);
  const hasPendingPlatformUpdates = pendingPlatformIds.length > 0;

  const sortedUsersForPlatform = useMemo(() => {
    if (!userSort?.dir || !userSort?.key) return platformUserRows;
    const compareText = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, { sensitivity: 'base' });
    const toName = (entry) => [entry.firstName, entry.lastName].filter(Boolean).join(' ') || 'No name';
    const valueOf = (entry) => {
      if (userSort.key === 'user') return toName(entry);
      if (userSort.key === 'email') return entry.email;
      if (userSort.key === 'status') return formatUserStatusLabel(entry);
      if (userSort.key === 'role') return entry.roleLabel || entry.roleCode || '';
      if (userSort.key === 'group') return formatUserGroupLabel(entry);
      if (userSort.key === 'customer') return formatUserCustomerLabel(entry);
      if (userSort.key === 'store') return formatUserStoreLabel(entry);
      return '';
    };
    const next = [...platformUserRows].sort((left, right) => {
      if (userSort.key === 'status') {
        const rank = (entry) => {
          const label = String(entry?.statusLabel || '').toLowerCase();
          if (label === 'unverified') return 0;
          return entry?.isActive ? 1 : 2;
        };
        return rank(left) - rank(right);
      }
      return compareText(valueOf(left), valueOf(right));
    });
    return userSort.dir === 'desc' ? next.reverse() : next;
  }, [platformUserRows, userSort]);


  const onToggleUserSort = (key) => {
    setUserSort((current) => {
      if (current.key !== key) return { key, dir: 'asc' };
      if (current.dir === 'asc') return { key, dir: 'desc' };
      if (current.dir === 'desc') return { key: '', dir: null };
      return { key, dir: 'asc' };
    });
  };

  if (!canSee) {
    return (
      <motion.section className="panel-card">
        <h2>Platform access required</h2>
        <p className="hint-text">Only HK-Admin, Group-Admin or Store-Admin can manage platform access.</p>
      </motion.section>
    );
  }

  return (
    <section className="admin-stack">
      <motion.article className="panel-card platform-access-panel">
        <div className="panel-title">
          <SlidersHorizontal size={16} />
          <h2>Platform Access</h2>
        </div>

        <div className="inline-row platform-mode-tabs" style={{ gap: 10, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {!isPureStoreAdmin && (
            <button
              type="button"
              className={mode === 'store' ? 'primary-button admin-tab-button' : 'ghost-button admin-tab-button'}
              onClick={() => setMode('store')}
            >
              Stores
            </button>
          )}
          {isHkAdmin && (
            <button
              type="button"
              className={mode === 'group' ? 'primary-button admin-tab-button' : 'ghost-button admin-tab-button'}
              onClick={() => setMode('group')}
            >
              Groups
            </button>
          )}
          <button
            type="button"
            className={mode === 'user' ? 'primary-button admin-tab-button' : 'ghost-button admin-tab-button'}
            onClick={() => setMode('user')}
          >
            Users
          </button>
        </div>

        {mode === 'user' && (
          <div className="admin-filters">
            <div className="search-input-wrap">
              <Search size={14} />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by user or email" />
            </div>
          </div>
        )}

        {mode !== 'user' && (
          <div
            className="admin-filters platform-scope-filters"
            style={{
              gridTemplateColumns:
                mode === 'group'
                  ? 'minmax(180px, 300px) auto'
                  : isHkAdmin
                    ? 'minmax(220px, 0.7fr) minmax(220px, 420px) auto'
                    : 'minmax(220px, 420px) auto',
            }}
          >
            {mode === 'group' && isHkAdmin && (
              <div className="multi-select-wrap" ref={groupsPickerRef}>
                <button
                  type="button"
                  className="multi-select-trigger"
                  onClick={() => setGroupsPickerOpen((v) => !v)}
                  aria-expanded={groupsPickerOpen}
                >
                  <span className="multi-select-label">
                    {selectedGroupIds.length === 0
                      ? 'Select groups'
                      : selectedGroupIds.length === 1
                        ? '1 group selected'
                        : `${selectedGroupIds.length} groups selected`}
                  </span>
                  <span className={groupsPickerOpen ? 'rounded-select-caret open' : 'rounded-select-caret'} />
                </button>
                {groupsPickerOpen && (
                  <div className="multi-select-menu">
                    <div className="multi-select-actions">
                      <button
                        type="button"
                        className="ghost-button multi-select-action"
                        onClick={() => setSelectedGroupIds(groups.map((g) => g.id))}
                      >
                        All
                      </button>
                      <button type="button" className="ghost-button multi-select-action" onClick={() => setSelectedGroupIds([])}>
                        Clear
                      </button>
                    </div>
                    <div className="multi-select-search">
                      <input
                        type="text"
                        value={groupsPickerQuery}
                        onChange={(event) => setGroupsPickerQuery(event.target.value)}
                        placeholder="Search groups..."
                      />
                    </div>
                    {filteredGroupsForPicker.map((g) => {
                      const checked = selectedGroupIds.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          className="multi-select-item"
                          onClick={() =>
                            setSelectedGroupIds((current) =>
                              current.includes(g.id) ? current.filter((id) => id !== g.id) : [...current, g.id]
                            )
                          }
                        >
                          <span>{g.name}</span>
                          <span className={checked ? 'multi-check on' : 'multi-check'}>{checked ? '✓' : ''}</span>
                        </button>
                      );
                    })}
                    {filteredGroupsForPicker.length === 0 && <div className="multi-select-empty">No groups found.</div>}
                  </div>
                )}
              </div>
            )}

            {mode === 'store' && isHkAdmin && (
              <RoundedSelect
                value={selectedGroupId}
                onChange={(val) => setSelectedGroupId(val)}
                placeholder="Filter by group (optional)"
                searchable
                searchPlaceholder="Search groups..."
                menuMaxHeight={240}
                options={[
                  { value: '', label: 'All groups' },
                  ...groups.map((g) => ({ value: String(g.id), label: g.name })),
                ]}
              />
            )}

            {mode === 'store' && (
              <div className="multi-select-wrap" ref={storesPickerRef}>
                <button
                  type="button"
                  className="multi-select-trigger"
                  onClick={() => setStoresPickerOpen((v) => !v)}
                  aria-expanded={storesPickerOpen}
                >
                  <span className="multi-select-label">
                    {selectedStoreIds.length === 0
                      ? 'Select stores'
                      : selectedStoreIds.length === 1
                        ? '1 store selected'
                        : `${selectedStoreIds.length} stores selected`}
                  </span>
                  <span className={storesPickerOpen ? 'rounded-select-caret open' : 'rounded-select-caret'} />
                </button>
                {storesPickerOpen && (
                  <div className="multi-select-menu">
                    <div className="multi-select-actions">
                      <button
                        type="button"
                        className="ghost-button multi-select-action"
                        onClick={() => setSelectedStoreIds(storesForSelectedGroup.map((s) => s.id))}
                      >
                        All
                      </button>
                      <button type="button" className="ghost-button multi-select-action" onClick={() => setSelectedStoreIds([])}>
                        Clear
                      </button>
                    </div>
                    <div className="multi-select-search">
                      <input
                        type="text"
                        value={storesPickerQuery}
                        onChange={(event) => setStoresPickerQuery(event.target.value)}
                        placeholder="Search stores..."
                      />
                    </div>
                    {filteredStoresForPicker.map((s) => {
                      const checked = selectedStoreIds.includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className="multi-select-item"
                          onClick={() =>
                            setSelectedStoreIds((current) =>
                              current.includes(s.id) ? current.filter((id) => id !== s.id) : [...current, s.id]
                            )
                          }
                        >
                          <span>{s.name}</span>
                          <span className={checked ? 'multi-check on' : 'multi-check'}>{checked ? '✓' : ''}</span>
                        </button>
                      );
                    })}
                    {filteredStoresForPicker.length === 0 && <div className="multi-select-empty">No stores found.</div>}
                  </div>
                )}
              </div>
            )}

            <button
              type="button"
              className="primary-button admin-inline-button platform-manage-button"
              disabled={
                isBusy ||
                hasPendingPlatformUpdates ||
                (mode === 'group' && (!isHkAdmin || selectedGroupIds.length === 0)) ||
                (mode === 'store' && selectedStoreIds.length === 0)
              }
              onClick={() => {
                if (mode === 'group') {
                  loadScopePlatforms({ nextMode: 'group', groupIds: selectedGroupIds });
                  return;
                }
                loadScopePlatforms({ nextMode: 'store', storeIds: selectedStoreIds });
              }}
            >
              Manage
            </button>
          </div>
        )}

        {error && <p className="error-text">{error}</p>}

        {mode === 'user' &&
          (isLoading && users.length === 0 ? (
            <div className="portal-loading-inline">Loading users...</div>
          ) : (
            <div className="table-wrap">
              <table className="permissions-table">
                <thead>
                  <tr>
                    {PLATFORM_USER_COLUMNS.map((column) => (
                      <th key={column.key}>
                        <button type="button" className="sortable-th-button" onClick={() => onToggleUserSort(column.key)}>
                          {column.label}{' '}
                          <span
                            className={
                              userSort.key === column.key ? `sort-indicator ${userSort.dir || 'none'}` : 'sort-indicator none'
                            }
                          />
                        </button>
                      </th>
                    ))}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsersForPlatform.map((entry) => (
                    <tr key={entry.__rowKey || entry.userId}>
                      <td data-label="User">
                        <div className="users-name-cell">
                          <PlatformUserAvatar entry={entry} />
                          <span>{getPlatformUserDisplayName(entry)}</span>
                        </div>
                      </td>
                      <td data-label="Email">{entry.email}</td>
                      <td data-label="Status">{formatUserStatusLabel(entry)}</td>
                      <td data-label="Role">{entry.roleLabel || entry.roleCode || '—'}</td>
                      <td data-label="Group">{formatUserGroupLabel(entry) || '—'}</td>
                      <td data-label="Customer">{formatUserCustomerLabel(entry) || '—'}</td>
                      <td data-label="Store">{formatUserStoreLabel(entry) || '—'}</td>
                      <td data-label="Actions" className="platform-user-actions-cell">
                        {canManageUserPlatformEntry(entry, scopeFilterContext) ? (
                          <button type="button" className="ghost-button admin-inline-button" onClick={() => openPlatforms(entry)} disabled={isBusy}>
                            Platforms
                          </button>
                        ) : (
                          <span className="hint-text">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </motion.article>

      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence mode="wait">
            {platformsOpen && (
              <motion.div className="modal-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <motion.div
                  className="modal-card platforms-modal"
                  initial={{ opacity: 0, y: 30, scale: 0.94 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 18, scale: 0.98 }}
                  transition={{ duration: 0.26, ease: [0.22, 1, 0.36, 1] }}
                >
                <h3>Platforms</h3>
                {platformsFor?.mode === 'user' ? (
                  <p className="hint-text">
                    Access for <strong>{platformsFor?.email}</strong>
                    {platformsFor?.groupName ? (
                      <>
                        {' '}
                        in <strong>{platformsFor.groupName}</strong>
                        {platformsFor?.storeName ? (
                          <>
                            {' '}
                            / <strong>{platformsFor.storeName}</strong>
                          </>
                        ) : null}
                      </>
                    ) : null}
                    .
                  </p>
                ) : (
                  <p className="hint-text">
                    {platformsFor?.mode === 'store' && !canEditStoreScope
                      ? 'Store platform access is read-only for Store-Admin. Contact Group-Admin or HK-Admin to change it.'
                      : 'Manage platform defaults for selected scope.'}
                  </p>
                )}
                {platformsError && <p className="error-text">{platformsError}</p>}
                <div className="platforms-toolbar">
                  <div className="search-input-wrap">
                    <Search size={14} />
                    <input
                      value={platformSearch}
                      onChange={(event) => setPlatformSearch(event.target.value)}
                      placeholder="Search platforms"
                      disabled={isBusy}
                    />
                  </div>
                  {platformsFor?.mode !== 'user' && (platformsFor?.mode !== 'store' || canEditStoreScope) && (
                    <>
                      <button
                        type="button"
                        className="ghost-button admin-inline-button"
                        onClick={() => saveScopePlatforms(platformsList.map((p) => ({ platformId: p.platformId, isEnabled: true })))}
                        disabled={isBusy || hasPendingPlatformUpdates || platformsList.length === 0}
                      >
                        Enable all
                      </button>
                      <button
                        type="button"
                        className="ghost-button admin-inline-button"
                        onClick={() => saveScopePlatforms(platformsList.map((p) => ({ platformId: p.platformId, isEnabled: false })))}
                        disabled={isBusy || hasPendingPlatformUpdates || platformsList.length === 0}
                      >
                        Disable all
                      </button>
                    </>
                  )}
                </div>
                {isBusy && platformsList.length === 0 ? (
                  <div className="portal-loading-inline">Loading platforms...</div>
                ) : (
                  <div className="modal-role-list platform-list-scroll">
                    {filteredPlatformsList.map((p) => (
                      <button
                        key={p.platformId}
                        type="button"
                        className={p.isEnabled ? 'modal-role-option selected' : 'modal-role-option'}
                        onClick={() => {
                          if (platformsFor?.mode === 'user') {
                            const nextEnabled =
                              p.accessState === 'mixed' ? true : !p.isEnabled;
                            togglePlatform(p.platformId, nextEnabled);
                          } else if (platformsFor?.mode === 'store' && !canEditStoreScope) {
                            return;
                          } else {
                            const nextEnabled =
                              platformsFor?.mode === 'store' && p.accessState === 'mixed'
                                ? true
                                : !p.isEnabled;
                            saveScopePlatforms([{ platformId: p.platformId, isEnabled: nextEnabled }]);
                          }
                        }}
                        disabled={
                          isBusy
                          || hasPendingPlatformUpdates
                          || (platformsFor?.mode === 'store' && !canEditStoreScope)
                        }
                      >
                        <span>{p.platformName}</span>
                        {platformsFor?.mode === 'user' ? (
                          <span
                            className={
                              p.accessState === 'mixed'
                                ? 'access-pill mixed'
                                : p.isEnabled
                                  ? 'access-pill enabled'
                                  : 'access-pill disabled'
                            }
                          >
                            {p.accessState === 'mixed'
                              ? 'Mixed'
                              : p.isEnabled
                                ? `Enabled (${p.source})`
                                : 'Disabled'}
                          </span>
                        ) : (
                          <span
                            className={
                              p.accessState === 'mixed'
                                ? 'access-pill mixed'
                                : p.isEnabled
                                  ? 'access-pill enabled'
                                  : 'access-pill disabled'
                            }
                          >
                            {p.accessState === 'mixed' ? 'Mixed' : p.isEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                        )}
                      </button>
                    ))}
                    {platformsList.length === 0 && !platformsError && <p className="hint-text">No platforms available.</p>}
                  </div>
                )}

                <div className="inline-row">
                  <button
                    type="button"
                    className="ghost-button admin-inline-button"
                    onClick={() => {
                      setPlatformsOpen(false);
                      setPlatformsFor(null);
                      setPlatformsList([]);
                      setPlatformsError('');
                      setPlatformSearch('');
                    }}
                    disabled={isBusy || hasPendingPlatformUpdates}
                  >
                    Close
                  </button>
                </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </section>
  );
}

